import { MessagingMessage, MessagingMessagesResponse } from "@/models/messaging/MessagingMessage";
import { MessagingParticipant } from "@/models/messaging/MessagingParticipant";
import LazoUserService from "@/services/LazoUserService";
import MessagingService from "@/services/messaging/MessagingService";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

export const MESSAGING_QUERY_KEY = "messaging";
export const KAM_CONVERSATIONS_QUERY_KEY = "kam-conversations";
export const KAM_CONVERSATION_MESSAGES_QUERY_KEY = "kam-conversation-messages";
export const CLIENT_CONVERSATIONS_QUERY_KEY = "client-conversations";
export const CLIENT_CONVERSATION_MESSAGES_QUERY_KEY = "client-conversation-messages";
export const CLIENT_CONVERSATION_SEARCH_QUERY_KEY = "client-conversation-search";
export const CLIENT_CONVERSATION_PARTICIPANTS_QUERY_KEY = "client-conversation-participants";
export const CONVERSATION_PARTICIPANTS_QUERY_KEY = "conversation-participants";
export const CLIENT_PARTICIPANTS_QUERY_KEY = "client-participants";
export const SERVICE_TEAM_USERS_QUERY_KEY = "service-team-users";
export const MESSAGING_UNREAD_COUNT_QUERY_KEY = "messaging-unread-count";
export const MESSAGE_SEARCH_QUERY_KEY = "messaging-search";
export const KAM_MESSAGE_SEARCH_QUERY_KEY = "kam-messaging-search";

export const INBOX_REDIRECT_QUERY_KEY = "messaging-inbox-redirect";

/**
 * The messaging-cutover switch. Once lazo-x owns the inbox this app stops receiving messages, so
 * its inbox screens must bounce instead of rendering a thread that will never update — and never
 * accept a reply that would go nowhere.
 *
 * Fails CLOSED (`enabled: false` while loading or on error): being late to redirect is a stale
 * screen, while redirecting on a failed probe would strand every user on a URL we could not read.
 */
export function useInboxRedirect() {
  return useQuery({
    queryKey: [INBOX_REDIRECT_QUERY_KEY],
    queryFn: () => MessagingService.getInboxRedirect().then((r) => r.getContentOrThrowError()),
    initialData: { enabled: false, url: "" },
    staleTime: 60_000,
    refetchOnWindowFocus: false,
    meta: {
      errorMessage: "Error loading the inbox configuration",
    },
  });
}

/**
 * Total messaging unread count for the authenticated user (client or KAM). Feeds the navbar inbox
 * badge alongside the legacy chat unread count. Invalidated live by the global NEW_MESSAGE handler.
 */
export function useMessagingUnreadCount() {
  return useQuery({
    queryKey: [MESSAGING_UNREAD_COUNT_QUERY_KEY],
    queryFn: () => MessagingService.getUnreadCount().then((r) => r.getContentOrThrowError().unread_count),
    initialData: 0,
    refetchOnWindowFocus: false,
    meta: {
      errorMessage: "Error loading your unread messages",
    },
  });
}

/**
 * "Next page" of an infinite messages query means the page of *older* messages: the first page is
 * the latest ~50, and each `fetchNextPage` walks backwards via a `before` cursor (the oldest loaded
 * message's timestamp). Returns `undefined` to stop when the server reports no more history.
 */
function olderPageParam(lastPage: MessagingMessagesResponse): string | undefined {
  if (!lastPage.has_more || lastPage.messages.length === 0) return undefined;
  return lastPage.messages[0].created_at; // messages are ascending → [0] is the oldest in the page
}

/**
 * Flattens infinite-query pages into a single chronological (ascending) list. Pages are stored
 * newest-first (page 0 = latest), so we reverse the page order before flattening; messages within a
 * page are already ascending.
 */
export function flattenMessagePages(data?: { pages: MessagingMessagesResponse[] }): MessagingMessage[] {
  if (!data) return [];
  return [...data.pages].reverse().flatMap((page) => page.messages);
}

/** The current conversation status/resolved_at ride on the latest page (page 0). */
export function latestMessagesPage(data?: { pages: MessagingMessagesResponse[] }): MessagingMessagesResponse | undefined {
  return data?.pages[0];
}

/**
 * "Next page" cursor for a search query. Search results come back newest-first (DESC), so the oldest
 * loaded result — the cursor for the next older page — is the LAST element of the page (unlike the
 * read path, where pages are ascending and [0] is the oldest).
 */
function searchOlderPageParam(lastPage: MessagingMessagesResponse): string | undefined {
  if (!lastPage.has_more || lastPage.messages.length === 0) return undefined;
  return lastPage.messages[lastPage.messages.length - 1].created_at;
}

/**
 * Flattens search-result pages into a single newest-first list. Each page is already DESC and pages
 * walk older, so a plain flatMap (no reverse) keeps the overall newest-first order for the panel.
 */
export function flattenSearchPages(data?: { pages: MessagingMessagesResponse[] }): MessagingMessage[] {
  if (!data) return [];
  return data.pages.flatMap((page) => page.messages);
}

/** Minimum query length before a search fires (avoids hammering the backend on 1-char input). */
export const MESSAGE_SEARCH_MIN_LENGTH = 2;

/**
 * Conversation history for the client's KAM thread, paginated backwards for upward infinite scroll.
 * Live updates arrive over WebSocket when the socket is healthy; a 5s polling fallback (plus refetch
 * on focus) keeps the latest page fresh even if the WS subscription has silently dropped. Only
 * mounted while the inbox is open, so polling stops automatically when the user leaves the view.
 */
export function useMessages(tenantExternalId?: string) {
  return useInfiniteQuery({
    queryKey: [MESSAGING_QUERY_KEY, tenantExternalId],
    queryFn: ({ pageParam }) =>
      MessagingService.getMessages(tenantExternalId!, { before: pageParam }).then((r) => r.getContentOrThrowError()),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: olderPageParam,
    enabled: !!tenantExternalId,
    refetchOnWindowFocus: true,
    refetchInterval: 5000,
    meta: {
      errorMessage: "Error loading your messages",
    },
  });
}

/**
 * Searches the client's KAM thread by content across the full history (not just loaded pages).
 * On-demand: only fires once the term reaches {@link MESSAGE_SEARCH_MIN_LENGTH}, no polling. Paged
 * backwards via the `before` cursor so the results panel can load more matches on scroll.
 */
export function useMessageSearch(tenantExternalId: string | undefined, query: string) {
  const term = query.trim();
  return useInfiniteQuery({
    queryKey: [MESSAGE_SEARCH_QUERY_KEY, tenantExternalId, term],
    queryFn: ({ pageParam }) =>
      MessagingService.searchMessages(tenantExternalId!, { q: term, before: pageParam }).then((r) => r.getContentOrThrowError()),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: searchOlderPageParam,
    enabled: !!tenantExternalId && term.length >= MESSAGE_SEARCH_MIN_LENGTH,
    refetchOnWindowFocus: false,
    meta: {
      errorMessage: "Error searching your messages",
    },
  });
}

/** Mutation variable for a message send: the (possibly empty) text plus any attached files. */
export interface SendMessageVariables {
  content: string;
  files?: File[];
}

export function useSendMessage(tenantExternalId?: string) {
  return useMutation({
    mutationFn: ({ content, files }: SendMessageVariables) =>
      MessagingService.sendMessage(tenantExternalId!, content, files).then((r) => r.getContentOrThrowError()),
    meta: {
      errorMessage: "No pudimos enviar tu mensaje",
    },
  });
}

// ── Client multi-conversation inbox (LazoOne) ───────────────────────────
// The client sees a list of their conversations (owned + invited) in the tenant, and opens each by
// external id. Mirrors the KAM inbox hooks, but every call is tenant-scoped.

/**
 * All conversations the client can see in a tenant (owned + actively-invited). Live updates arrive
 * over WebSocket when healthy; a 10s polling fallback (plus refetch on focus) keeps the list fresh.
 */
export function useClientConversations(tenantExternalId?: string, status?: string) {
  return useQuery({
    queryKey: [CLIENT_CONVERSATIONS_QUERY_KEY, tenantExternalId, status ?? "all"],
    queryFn: () => MessagingService.listClientConversations(tenantExternalId!, status).then((r) => r.getContentOrThrowError()),
    enabled: !!tenantExternalId,
    refetchOnWindowFocus: true,
    refetchInterval: 10000,
    meta: {
      errorMessage: "Error loading your conversations",
    },
  });
}

/**
 * Message history of a single client conversation, paginated backwards for upward infinite scroll.
 * Disabled until a conversation is selected. Live updates arrive over WebSocket when healthy; a 5s
 * polling fallback (plus refetch on focus) keeps the open thread fresh.
 */
export function useClientConversationMessages(tenantExternalId?: string, conversationExternalId?: string) {
  return useInfiniteQuery({
    queryKey: [CLIENT_CONVERSATION_MESSAGES_QUERY_KEY, tenantExternalId, conversationExternalId],
    queryFn: ({ pageParam }) =>
      MessagingService.getClientConversationMessages(tenantExternalId!, conversationExternalId!, { before: pageParam }).then((r) =>
        r.getContentOrThrowError()
      ),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: olderPageParam,
    enabled: !!tenantExternalId && !!conversationExternalId,
    refetchOnWindowFocus: true,
    refetchInterval: 5000,
    meta: {
      errorMessage: "Error loading the conversation",
    },
  });
}

/** Searches a single client conversation by content across its full history (on-demand, backward-paged). */
export function useClientConversationSearch(
  tenantExternalId: string | undefined,
  conversationExternalId: string | undefined,
  query: string
) {
  const term = query.trim();
  return useInfiniteQuery({
    queryKey: [CLIENT_CONVERSATION_SEARCH_QUERY_KEY, tenantExternalId, conversationExternalId, term],
    queryFn: ({ pageParam }) =>
      MessagingService.searchClientConversationMessages(tenantExternalId!, conversationExternalId!, { q: term, before: pageParam }).then(
        (r) => r.getContentOrThrowError()
      ),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: searchOlderPageParam,
    enabled: !!tenantExternalId && !!conversationExternalId && term.length >= MESSAGE_SEARCH_MIN_LENGTH,
    refetchOnWindowFocus: false,
    meta: {
      errorMessage: "Error searching the conversation",
    },
  });
}

export function useSendClientConversationMessage(tenantExternalId?: string, conversationExternalId?: string) {
  return useMutation({
    mutationFn: ({ content, files }: SendMessageVariables) =>
      MessagingService.sendClientConversationMessage(tenantExternalId!, conversationExternalId!, content, files).then((r) =>
        r.getContentOrThrowError()
      ),
    meta: {
      errorMessage: "No pudimos enviar tu mensaje",
    },
  });
}

export function useMarkClientConversationRead(tenantExternalId?: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (conversationExternalId: string) =>
      MessagingService.markClientConversationRead(tenantExternalId!, conversationExternalId).then((r) => r.getContentOrThrowError()),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CLIENT_CONVERSATIONS_QUERY_KEY, tenantExternalId] });
      queryClient.invalidateQueries({ queryKey: [MESSAGING_UNREAD_COUNT_QUERY_KEY] });
    },
    meta: {
      errorMessage: "We couldn't mark the conversation as read",
    },
  });
}

/** Active participants of a specific client conversation (read-only chips). Disabled until selected. */
export function useClientConversationParticipants(tenantExternalId?: string, conversationExternalId?: string) {
  return useQuery({
    queryKey: [CLIENT_CONVERSATION_PARTICIPANTS_QUERY_KEY, tenantExternalId, conversationExternalId],
    queryFn: () =>
      MessagingService.getClientConversationParticipants(tenantExternalId!, conversationExternalId!).then((r) =>
        normalizeParticipants(r.getContentOrThrowError())
      ),
    enabled: !!tenantExternalId && !!conversationExternalId,
    refetchOnWindowFocus: false,
    meta: {
      errorMessage: "Error loading the conversation participants",
    },
  });
}

// ── KAM inbox (agent side) ──────────────────────────────────────────────

/**
 * All conversations assigned to the current KAM. Live updates arrive over WebSocket when healthy; a
 * 10s polling fallback (plus refetch on focus) keeps the list fresh if the WS subscription dropped.
 * Only mounted while the KAM inbox is open, so polling stops automatically when the user leaves.
 */
export function useKamConversations(status?: string) {
  return useQuery({
    queryKey: [KAM_CONVERSATIONS_QUERY_KEY, status ?? "all"],
    queryFn: () => MessagingService.listConversations(status).then((r) => r.getContentOrThrowError()),
    refetchOnWindowFocus: true,
    refetchInterval: 10000,
    meta: {
      errorMessage: "Error loading your conversations",
    },
  });
}

/**
 * Message history of a single conversation (KAM open thread). Disabled until a conversation is
 * selected. Live updates arrive over WebSocket when healthy; a 5s polling fallback (plus refetch on
 * focus) keeps the open thread fresh if the WS subscription dropped. Only mounted while the thread is
 * open, so polling stops automatically when the user leaves.
 */
export function useKamConversationMessages(conversationExternalId?: string) {
  return useInfiniteQuery({
    queryKey: [KAM_CONVERSATION_MESSAGES_QUERY_KEY, conversationExternalId],
    queryFn: ({ pageParam }) =>
      MessagingService.getConversationMessages(conversationExternalId!, { before: pageParam }).then((r) => r.getContentOrThrowError()),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: olderPageParam,
    enabled: !!conversationExternalId,
    refetchOnWindowFocus: true,
    refetchInterval: 5000,
    meta: {
      errorMessage: "Error loading the conversation",
    },
  });
}

/**
 * Searches a single KAM conversation by content across the full history. Same on-demand, backward-
 * paged shape as {@link useMessageSearch}; disabled until a conversation is selected and the term is
 * long enough.
 */
export function useKamMessageSearch(conversationExternalId: string | undefined, query: string) {
  const term = query.trim();
  return useInfiniteQuery({
    queryKey: [KAM_MESSAGE_SEARCH_QUERY_KEY, conversationExternalId, term],
    queryFn: ({ pageParam }) =>
      MessagingService.searchConversationMessages(conversationExternalId!, { q: term, before: pageParam }).then((r) =>
        r.getContentOrThrowError()
      ),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: searchOlderPageParam,
    enabled: !!conversationExternalId && term.length >= MESSAGE_SEARCH_MIN_LENGTH,
    refetchOnWindowFocus: false,
    meta: {
      errorMessage: "Error searching the conversation",
    },
  });
}

export function useSendKamReply(conversationExternalId?: string) {
  return useMutation({
    mutationFn: ({ content, files }: SendMessageVariables) =>
      MessagingService.sendKamReply(conversationExternalId!, content, files).then((r) => r.getContentOrThrowError()),
    meta: {
      errorMessage: "We couldn't send your reply",
    },
  });
}

/**
 * Saves a message attachment into the tenant's Document Center. On success, refetches the
 * conversation's messages so the attachment's "Saved" badge appears.
 */
export function useSaveAttachmentToDocumentCenter(conversationExternalId?: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      attachmentExternalId,
      folderExternalId,
      fileName,
    }: {
      attachmentExternalId: string;
      folderExternalId: string;
      fileName?: string;
    }) =>
      MessagingService.saveAttachmentToDocumentCenter(conversationExternalId!, attachmentExternalId, folderExternalId, fileName).then((r) =>
        r.getContentOrThrowError()
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [KAM_CONVERSATION_MESSAGES_QUERY_KEY, conversationExternalId] });
    },
    meta: {
      successMessage: "Saved to Document Center",
      errorMessage: "We couldn't save the attachment to the Document Center",
    },
  });
}

export function useMarkConversationRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (conversationExternalId: string) =>
      MessagingService.markConversationRead(conversationExternalId).then((r) => r.getContentOrThrowError()),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [KAM_CONVERSATIONS_QUERY_KEY] });
    },
    meta: {
      errorMessage: "We couldn't mark the conversation as read",
    },
  });
}

// ── Ops participants (add/remove members) ───────────────────────────────

/** Normalize the participants endpoint, which may return a wrapped object or a bare array. */
function normalizeParticipants(payload: { participants?: MessagingParticipant[] } | MessagingParticipant[]): MessagingParticipant[] {
  if (Array.isArray(payload)) return payload;
  return payload?.participants ?? [];
}

/** Current participants of a conversation. Disabled until a conversation is selected. */
export function useConversationParticipants(conversationExternalId?: string) {
  return useQuery({
    queryKey: [CONVERSATION_PARTICIPANTS_QUERY_KEY, conversationExternalId],
    queryFn: () => MessagingService.getParticipants(conversationExternalId!).then((r) => normalizeParticipants(r.getContentOrThrowError())),
    enabled: !!conversationExternalId,
    refetchOnWindowFocus: false,
    meta: {
      errorMessage: "Error loading the conversation participants",
    },
  });
}

export function useAddParticipants(conversationExternalId?: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ userExternalIds, seeFullHistory }: { userExternalIds: string[]; seeFullHistory?: boolean }) =>
      MessagingService.addParticipants(conversationExternalId!, userExternalIds, seeFullHistory ?? false).then((r) =>
        r.getContentOrThrowError()
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CONVERSATION_PARTICIPANTS_QUERY_KEY, conversationExternalId] });
      queryClient.invalidateQueries({ queryKey: [KAM_CONVERSATIONS_QUERY_KEY] });
    },
    meta: {
      errorMessage: "We couldn't add the member",
    },
  });
}

export function useRemoveParticipant(conversationExternalId?: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (userExternalId: string) =>
      MessagingService.removeParticipant(conversationExternalId!, userExternalId).then((r) => r.getContentOrThrowError()),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CONVERSATION_PARTICIPANTS_QUERY_KEY, conversationExternalId] });
      queryClient.invalidateQueries({ queryKey: [KAM_CONVERSATIONS_QUERY_KEY] });
    },
    meta: {
      errorMessage: "We couldn't remove the member",
    },
  });
}

/**
 * The current KAM-portal viewer leaves a conversation they were invited to. Refetches the
 * conversation list, the open thread and the participants so the read-only state (banner, greyed
 * row, "left" system line) surfaces immediately.
 */
export function useLeaveKamConversation(conversationExternalId?: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => MessagingService.leaveConversation(conversationExternalId!).then((r) => r.getContentOrThrowError()),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CONVERSATION_PARTICIPANTS_QUERY_KEY, conversationExternalId] });
      queryClient.invalidateQueries({ queryKey: [KAM_CONVERSATION_MESSAGES_QUERY_KEY, conversationExternalId] });
      queryClient.invalidateQueries({ queryKey: [KAM_CONVERSATIONS_QUERY_KEY] });
    },
    meta: {
      errorMessage: "We couldn't leave the conversation",
    },
  });
}

// ── Client participants (tenant-scoped, LazoOne) ────────────────────────

/** Current participants of the caller's own KAM thread (client side). Disabled until a tenant is set. */
export function useClientParticipants(tenantExternalId?: string) {
  return useQuery({
    queryKey: [CLIENT_PARTICIPANTS_QUERY_KEY, tenantExternalId],
    queryFn: () => MessagingService.getClientParticipants(tenantExternalId!).then((r) => normalizeParticipants(r.getContentOrThrowError())),
    enabled: !!tenantExternalId,
    refetchOnWindowFocus: false,
    meta: {
      errorMessage: "Error loading the conversation participants",
    },
  });
}

/** Adds client contacts (same company) to the caller's thread; refetches the participant list. */
export function useAddClientContacts(tenantExternalId?: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ userExternalIds, seeFullHistory }: { userExternalIds: string[]; seeFullHistory?: boolean }) =>
      MessagingService.addClientContactParticipants(tenantExternalId!, userExternalIds, seeFullHistory ?? false).then((r) =>
        r.getContentOrThrowError()
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CLIENT_PARTICIPANTS_QUERY_KEY, tenantExternalId] });
      queryClient.invalidateQueries({ queryKey: [MESSAGING_QUERY_KEY, tenantExternalId] });
    },
    meta: {
      errorMessage: "We couldn't add the contact",
    },
  });
}

/** Removes a client contact from the caller's thread; refetches the participant list. */
export function useRemoveClientContact(tenantExternalId?: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (userExternalId: string) =>
      MessagingService.removeClientContactParticipant(tenantExternalId!, userExternalId).then((r) => r.getContentOrThrowError()),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CLIENT_PARTICIPANTS_QUERY_KEY, tenantExternalId] });
      queryClient.invalidateQueries({ queryKey: [MESSAGING_QUERY_KEY, tenantExternalId] });
    },
    meta: {
      errorMessage: "We couldn't remove the contact",
    },
  });
}

/**
 * The current client viewer leaves a conversation they were invited to. Refetches the client
 * conversation list, the open thread and the participants so the read-only state (banner, greyed
 * row, "left" system line) surfaces immediately.
 */
export function useLeaveClientConversation(tenantExternalId?: string, conversationExternalId?: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      MessagingService.leaveClientConversation(tenantExternalId!, conversationExternalId!).then((r) => r.getContentOrThrowError()),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CLIENT_CONVERSATION_PARTICIPANTS_QUERY_KEY, tenantExternalId, conversationExternalId] });
      queryClient.invalidateQueries({ queryKey: [CLIENT_CONVERSATION_MESSAGES_QUERY_KEY, tenantExternalId, conversationExternalId] });
      queryClient.invalidateQueries({ queryKey: [CLIENT_CONVERSATIONS_QUERY_KEY, tenantExternalId] });
    },
    meta: {
      errorMessage: "We couldn't leave the conversation",
    },
  });
}

/** Ops members pool for the participant picker. */
export function useServiceTeamUsers() {
  return useQuery({
    queryKey: [SERVICE_TEAM_USERS_QUERY_KEY],
    queryFn: () => LazoUserService.getServiceTeamUsers().then((r) => r.getContentOrThrowError()),
    refetchOnWindowFocus: false,
    meta: {
      errorMessage: "Error loading the service team",
    },
  });
}
